// NoticeBell — the bell in the top bar (owner, 29 Oct 2026: "that is not an
// alert, it is information"). What used to sit at the top of the Dashboard
// lives here: ALERTS, something a person must act on, counted on the bell;
// and HINTS, worth knowing and needing nothing, listed quietly below them.
// Each opens the page to act on. From GET /api/dashboard/notices, in the
// signed-in school; looked at again every five minutes and on each opening.
// @story HIR-HD2

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Button, Icon } from '../../ds';

const EVERY_MS = 5 * 60 * 1000;

export default function NoticeBell() {
  const navigate = useNavigate();
  const [state, setState] = useState({ alerts: 0, notices: [], loaded: false, failed: false });
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  const load = useCallback(() => api.get('/dashboard/notices')
    .then((r) => setState({ alerts: r.data.alerts || 0, notices: r.data.notices || [], loaded: true, failed: false }))
    .catch(() => setState((s) => ({ ...s, loaded: true, failed: true }))), []);

  useEffect(() => {
    load();
    const t = setInterval(load, EVERY_MS);
    return () => clearInterval(t);
  }, [load]);

  // Closes on a click outside it, or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', esc); };
  }, [open]);

  const toggle = () => { if (!open) load(); setOpen((o) => !o); };
  const go = (to) => { setOpen(false); if (to) navigate(to); };
  const alerts = state.notices.filter((n) => n.kind === 'alert');
  const hints = state.notices.filter((n) => n.kind !== 'alert');
  const label = state.alerts ? `Notices: ${state.alerts} to act on` : 'Notices';

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <Button variant="ghost" icon={<Icon name="bell" size={18} />} onClick={toggle} aria-label={label} aria-expanded={open} title={label}>
        {state.alerts > 0 && (
          <span data-testid="notice-count" style={{
            minWidth: 20, height: 20, padding: '0 6px', borderRadius: 'var(--radius-pill)', display: 'inline-grid', placeItems: 'center',
            background: 'var(--status-danger-text)', color: '#fff', fontSize: 'var(--fs-caption)', fontWeight: 600,
          }}>{state.alerts}</span>
        )}
      </Button>
      {open && (
        <div role="dialog" aria-label="Notices" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 380, maxWidth: '92vw', maxHeight: '70vh', overflowY: 'auto', zIndex: 60,
          background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-overlay)',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Notices</div>
          {state.failed && <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>Could not load the notices just now.</div>}
          {state.loaded && !state.failed && !state.notices.length && (
            <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>Nothing needs you right now.</div>
          )}
          {alerts.length > 0 && <Group title="To act on" items={alerts} tone="alert" onGo={go} />}
          {hints.length > 0 && <Group title="Good to know" items={hints} tone="hint" onGo={go} />}
        </div>
      )}
    </div>
  );
}

function Group({ title, items, tone, onGo }) {
  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ padding: '4px 16px', fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
      {items.map((n) => (
        <button key={n.key} type="button" onClick={() => onGo(n.to)} style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', textAlign: 'left', padding: '10px 16px',
          background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-body-sm)',
          color: tone === 'alert' ? 'var(--text-1)' : 'var(--text-muted)',
        }}>
          <span style={{ color: tone === 'alert' ? 'var(--status-danger-text)' : 'var(--status-info-text)', marginTop: 1 }}>
            <Icon name={tone === 'alert' ? 'warning-circle' : 'info'} size={16} />
          </span>
          <span style={{ flex: 1 }}>{n.text}</span>
          <Icon name="caret-right" size={14} />
        </button>
      ))}
    </div>
  );
}
